import React, { useEffect, useState } from 'react';
import { api, TenantFeatureRow } from '../services/api';
import { useApiData } from '../hooks/useApiData';
import { LoadingState, ErrorState, EmptyState } from '../components/primitives';

type FeatureKey = 'liveIntelligence' | 'training' | 'livePlaybook';

const FEATURE_META: { key: FeatureKey; label: string; desc: string }[] = [
  {
    key: 'liveIntelligence',
    label: 'Live intelligence',
    desc: 'Real-time objection / landmine / competitor suggestions during calls.'
  },
  {
    key: 'training',
    label: 'Training',
    desc: 'AI-simulated training scenarios and training analytics.'
  },
  {
    // Separate from live intelligence on purpose: this ships the discovery
    // checklist WITHOUT bringing back objections, landmines, competitors, KB
    // generation or vector sync.
    key: 'livePlaybook',
    label: 'Live discovery checklist',
    desc: 'Real-time coaching against the discovery playbook, graded afterwards by the same rubric.'
  }
];

/** On/off pill toggle styled with the dashboard design tokens. */
function Toggle({
  on,
  busy,
  onClick
}: {
  on: boolean;
  busy: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-pressed={on}
      style={{
        position: 'relative',
        width: 44,
        height: 24,
        borderRadius: 999,
        border: '1px solid var(--line)',
        background: on ? 'var(--accent)' : 'var(--surface-2)',
        cursor: busy ? 'wait' : 'pointer',
        opacity: busy ? 0.6 : 1,
        transition: 'background 160ms ease'
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: on ? 22 : 2,
          width: 18,
          height: 18,
          borderRadius: 999,
          background: on ? 'var(--accent-soft)' : 'var(--ink-4)',
          transition: 'left 160ms ease'
        }}
      />
    </button>
  );
}

export default function Features() {
  const { data, loading, error } = useApiData(() => api.getTenants(), []);
  const [rows, setRows] = useState<TenantFeatureRow[]>([]);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (data?.data?.tenants) {
      setRows(data.data.tenants);
    }
  }, [data]);

  const toggle = async (tenant: TenantFeatureRow, key: FeatureKey) => {
    const next = !tenant.features[key];
    const cellKey = `${tenant.tenantId}:${key}`;
    setBusyKey(cellKey);
    setSaveError(null);

    // Optimistic update
    setRows((prev) =>
      prev.map((r) =>
        r.tenantId === tenant.tenantId
          ? { ...r, features: { ...r.features, [key]: next } }
          : r
      )
    );

    try {
      const res = await api.updateTenantFeatures(tenant.tenantId, { [key]: next });
      // Reconcile with server truth
      setRows((prev) =>
        prev.map((r) =>
          r.tenantId === tenant.tenantId ? { ...r, features: res.data.features } : r
        )
      );
    } catch (e: any) {
      // Revert on failure
      setRows((prev) =>
        prev.map((r) =>
          r.tenantId === tenant.tenantId
            ? { ...r, features: { ...r.features, [key]: !next } }
            : r
        )
      );
      setSaveError(e?.message || 'Could not save the change.');
    } finally {
      setBusyKey(null);
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <>
      <div className="head">
        <div>
          <div className="eyebrow">Access control</div>
          <h1 className="title">
            <span className="serif">Feature toggles per client</span>
          </h1>
          <p className="subtitle">
            Turn capabilities on or off for each tenant. Changes persist to the tenant
            registry and take effect the next time a client app resolves its config.
          </p>
        </div>
      </div>

      {saveError && (
        <div className="error-state" style={{ marginBottom: 16 }}>
          {saveError}
        </div>
      )}

      <section className="section">
        <div className="section-head">
          <div>
            <h3>Clients</h3>
            <p className="desc">Lushin ships post-call-only by default; flip a flag to enable more.</p>
          </div>
        </div>
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                {FEATURE_META.map((f) => (
                  <th key={f.key} style={{ width: 200 }}>
                    {f.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((tenant) => (
                <tr key={tenant.tenantId}>
                  <td>
                    <span style={{ color: 'var(--ink)', fontWeight: 500 }}>
                      {tenant.displayName}
                    </span>
                    <span className="mono" style={{ color: 'var(--ink-3)', marginLeft: 8 }}>
                      {tenant.tenantId}
                    </span>
                  </td>
                  {FEATURE_META.map((f) => {
                    const cellKey = `${tenant.tenantId}:${f.key}`;
                    return (
                      <td key={f.key}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Toggle
                            on={!!tenant.features[f.key]}
                            busy={busyKey === cellKey}
                            onClick={() => toggle(tenant, f.key)}
                          />
                          <span className="chip" style={{ minWidth: 36, textAlign: 'center' }}>
                            {tenant.features[f.key] ? 'On' : 'Off'}
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={1 + FEATURE_META.length}>
                    <EmptyState>
                      <b>No tenants found.</b> Bootstrap a client first.
                    </EmptyState>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="desc" style={{ marginTop: 12 }}>
          {FEATURE_META.map((f) => (
            <span key={f.key} style={{ display: 'block' }}>
              <b>{f.label}:</b> {f.desc}
            </span>
          ))}
        </p>
      </section>
    </>
  );
}
